import HttpService from './HttpService';
import type { ItemResponse, ItemsResponse } from '@frontend/Models/Http/HttpModels';
import {
  pathOrganizations,
  pathOrganizationId,
  pathOrganizationMembers,
  pathOrganizationMemberId,
  pathOrganizationMerge,
  pathOrganizationsByMember,
  pathOrganizationSummary,
  pathOrganizationMsaRates,
  pathOrganizationMsaRateId,
} from '../../__settings__/septSoftwarePaths';

export interface Organization {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
  member_clientids?: string[];
}

export interface OrganizationMember {
  clientid: string;
  firstname: string | null;
  lastname: string | null;
  company: string | null;
  email: string | null;
  is_primary: boolean;
}

export interface OrganizationSummary {
  counts: { members: number; matters: number; bids: number };
  /** The org's matter ids in one round trip — what retires the Orbit N+1 fan-out. */
  matterids: string[];
  /** Present ONLY for financially-visible callers (absent, not zeroed — the server's
   * strip_financials doctrine). */
  financials?: { invoice_count: number; invoiced_total: string };
}

export interface OrgMsaRate {
  id: string;
  kind: 'labour' | 'equipment';
  basis: 't_and_m' | 'force_account';
  /** Absent for a blind ADMIN_CATALOGS caller (the write response strips). */
  rate?: string;
  labour_role_id: string | null;
  equipment_item_id: string | null;
  name: string | null;
  updated_at: string | null;
}

export interface UpsertOrgMsaRateBody {
  kind: 'labour' | 'equipment';
  labour_role_id?: string;
  equipment_item_id?: string;
  basis: 't_and_m' | 'force_account';
  rate: number;
}

const http = new HttpService();

class OrganizationService {
  static async getOrganizations(activeOnly = false): Promise<Organization[]> {
    const suffix = activeOnly ? '?active_only=true' : '';
    const response = await http.get<ItemsResponse<Organization>>(`${pathOrganizations}${suffix}`);
    return response.data.items;
  }

  static async getOrganization(orgId: string): Promise<Organization> {
    const response = await http.get<ItemResponse<Organization>>(pathOrganizationId(orgId));
    return response.data.item;
  }

  /** Orgs a client belongs to — the legacy `org-<clientid>` deep-link shim resolves here. */
  static async getOrganizationsByMember(clientId: string): Promise<Organization[]> {
    const response = await http.get<ItemsResponse<Organization>>(pathOrganizationsByMember(clientId));
    return response.data.items;
  }

  static async updateOrganization(orgId: string, name: string): Promise<Organization> {
    const response = await http.put<ItemResponse<Organization>>(pathOrganizationId(orgId), { name });
    return response.data.item;
  }

  static async getMembers(orgId: string): Promise<OrganizationMember[]> {
    const response = await http.get<ItemsResponse<OrganizationMember>>(pathOrganizationMembers(orgId));
    return response.data.items;
  }

  /** The join-table link that retires PUT-the-whole-client from the UI (P2 Build J). */
  static async addMember(orgId: string, clientId: string, isPrimary = false): Promise<void> {
    await http.post(pathOrganizationMembers(orgId), { clientid: clientId, is_primary: isPrimary });
  }

  static async removeMember(orgId: string, clientId: string): Promise<void> {
    await http.delete(pathOrganizationMemberId(orgId, clientId));
  }

  static async mergeOrganizations(survivorId: string, sourceId: string): Promise<void> {
    await http.post(pathOrganizationMerge(survivorId), { source_organization_id: sourceId });
  }

  /** Counts + matter ids in ONE round trip (P2 Build M) — the getClients + getAgents +
   * N× getClientMatters fan-out retires behind this. Server-gated {1,2}: callers below
   * that wall fall back to the legacy path. */
  static async getSummary(orgId: string): Promise<OrganizationSummary> {
    const response = await http.get<{ data: OrganizationSummary }>(pathOrganizationSummary(orgId));
    return response.data;
  }

  /** The org's MSA rate card — SEE_FINANCIALS on the server (403 for a blind caller). */
  static async getMsaRates(orgId: string): Promise<OrgMsaRate[]> {
    const response = await http.get<ItemsResponse<OrgMsaRate>>(pathOrganizationMsaRates(orgId));
    return response.data.items;
  }

  /** Set one cell (create-or-replace on the (org, kind, ref, basis) key). */
  static async upsertMsaRate(orgId: string, body: UpsertOrgMsaRateBody): Promise<OrgMsaRate> {
    const response = await http.post<ItemResponse<OrgMsaRate>>(
      pathOrganizationMsaRates(orgId), body,
    );
    return response.data.item;
  }

  /** Remove one cell — FA pricing falls back to the catalog rung, disclosed per line. */
  static async deleteMsaRate(orgId: string, rateId: string): Promise<void> {
    await http.delete(pathOrganizationMsaRateId(orgId, rateId));
  }
}

export default OrganizationService;
